// Scenario Types
export interface Scenario {
  id: string;
  name: string;
  description?: string;
  category: ScenarioCategory;
  riskLevel: RiskLevel;
  timeFrame: TimeFrame;
  userId: string;
  isTemplate?: boolean;
  templateId?: string;
  isActive: boolean;
  createdAt: Date;
  updatedAt: Date;
  entryRules?: EntryRule[];
  exitRules?: ExitRule[];
  riskManagement?: RiskManagement;
  performance?: ScenarioPerformance;
}

export interface CreateScenarioDto {
  name: string;
  description?: string;
  category: ScenarioCategory;
  riskLevel: RiskLevel;
  timeFrame: TimeFrame;
  userId: string;
  isTemplate?: boolean;
  templateId?: string;
  entryRules?: EntryRuleInput[];
  exitRules?: ExitRuleInput[];
  riskManagement?: RiskManagementInput;
}

export interface UpdateScenarioDto {
  name?: string;
  description?: string;
  category?: ScenarioCategory;
  riskLevel?: RiskLevel;
  timeFrame?: TimeFrame;
  isActive?: boolean;
  entryRules?: EntryRuleInput[];
  exitRules?: ExitRuleInput[];
  riskManagement?: RiskManagementInput;
}

export interface ScenarioFilterDto {
  category?: ScenarioCategory;
  riskLevel?: RiskLevel;
  timeFrame?: TimeFrame;
  isActive?: boolean;
  isTemplate?: boolean;
  userId?: string;
  page?: number;
  limit?: number;
}

// Template Types
export interface ScenarioTemplate {
  id: string;
  name: string;
  description: string;
  category: ScenarioCategory;
  riskLevel: RiskLevel;
  timeFrame: TimeFrame;
  version?: string;
  author?: string;
  tags?: string[];
  difficulty?: TemplateDifficulty;
  estimatedReturn?: number;
  maxDrawdown?: number;
  timeHorizon?: TimeHorizon;
  marketConditions?: string[];
  assetClasses?: string[];
  minCapital?: number;
  maxCapital?: number;
  riskRewardRatio?: number;
  successRate?: number;
  backtestResults?: Record<string, any>;
  notes?: string;
  entryRules?: TemplateRule[];
  exitRules?: TemplateRule[];
  riskManagement?: TemplateRiskManagement;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateTemplateInput {
  name: string;
  description: string;
  category: ScenarioCategory;
  riskLevel: RiskLevel;
  timeFrame: TimeFrame;
  version?: string;
  author?: string;
  tags?: string[];
  difficulty?: TemplateDifficulty;
  estimatedReturn?: number;
  maxDrawdown?: number;
  timeHorizon?: TimeHorizon;
  marketConditions?: string[];
  assetClasses?: string[];
  minCapital?: number;
  maxCapital?: number;
  riskRewardRatio?: number;
  successRate?: number;
  backtestResults?: Record<string, any>;
  notes?: string;
  entryRules?: TemplateRuleInput[];
  exitRules?: TemplateRuleInput[];
  riskManagement?: TemplateRiskManagementInput;
}

export interface UpdateTemplateInput {
  name?: string;
  description?: string;
  category?: ScenarioCategory;
  riskLevel?: RiskLevel;
  timeFrame?: TimeFrame;
  version?: string;
  tags?: string[];
  difficulty?: TemplateDifficulty;
  estimatedReturn?: number;
  maxDrawdown?: number;
  timeHorizon?: TimeHorizon;
  marketConditions?: string[];
  assetClasses?: string[];
  minCapital?: number;
  maxCapital?: number;
  riskRewardRatio?: number;
  successRate?: number;
  backtestResults?: Record<string, any>;
  notes?: string;
}

// Performance Types
export interface ScenarioPerformance {
  id: string;
  scenarioId: string;
  totalTrades: number;
  winningTrades: number;
  losingTrades: number;
  totalProfit: number;
  totalLoss: number;
  netProfit: number;
  winRate: number;
  averageWin: number;
  averageLoss: number;
  profitFactor: number;
  maxDrawdown: number;
  sharpeRatio: number;
  sortinoRatio: number;
  calmarRatio: number;
  dailyPerformance: Record<string, any>;
  monthlyPerformance: Record<string, any>;
  lastUpdated: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface PerformanceMetrics {
  totalTrades: number;
  winningTrades: number;
  losingTrades: number;
  totalProfit: number;
  totalLoss: number;
  netProfit: number;
  winRate: number;
  averageWin: number;
  averageLoss: number;
  profitFactor: number;
  maxDrawdown: number;
  sharpeRatio: number;
  sortinoRatio: number;
  calmarRatio: number;
  lastUpdated: Date;
}

export interface ExecutionStats {
  totalExecutions: number;
  successfulExecutions: number;
  failedExecutions: number;
  averageExecutionTime: number;
  lastExecution: Date;
  lastUpdated: Date;
}

export interface DateRange {
  start: Date;
  end: Date;
}

export interface PerformanceFilter {
  startDate?: Date;
  endDate?: Date;
  minProfit?: number;
  maxDrawdown?: number;
  minWinRate?: number;
}

// Rule Types
export interface EntryRule {
  id: string;
  name: string;
  description: string;
  type: RuleType;
  parameters: Record<string, any>;
  priority: number;
  isActive: boolean;
  condition: string;
  scenarioId: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface ExitRule {
  id: string;
  name: string;
  description: string;
  type: ExitType;
  parameters: Record<string, any>;
  priority: number;
  isActive: boolean;
  condition: string;
  value?: number;
  scenarioId: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface EntryRuleInput {
  name: string;
  description: string;
  type: RuleType;
  parameters: Record<string, any>;
  priority: number;
  isActive: boolean;
  condition: string;
}

export interface ExitRuleInput {
  name: string;
  description: string;
  type: ExitType;
  parameters: Record<string, any>;
  priority: number;
  isActive: boolean;
  condition: string;
  value?: number;
}

export interface TemplateRule {
  name: string;
  description: string;
  type: string;
  parameters: Record<string, any>;
  priority: number;
  isActive: boolean;
  condition: string;
  value?: number;
}

export interface TemplateRuleInput {
  name: string;
  description: string;
  type: string;
  parameters: Record<string, any>;
  priority: number;
  isActive: boolean;
  condition: string;
  value?: number;
}

// Risk Management Types
export interface RiskManagement {
  id: string;
  scenarioId: string;
  maxPositionSize: number;
  maxOpenPositions: number;
  maxDailyLoss: number;
  maxWeeklyLoss: number;
  maxDrawdown: number;
  stopLossPercentage: number;
  positionSizingMethod: string;
  maxCorrelation: number;
  maxSectorExposure: number;
  volatilityAdjustment: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface RiskManagementInput {
  maxPositionSize: number;
  maxOpenPositions: number;
  maxDailyLoss: number;
  maxWeeklyLoss: number;
  maxDrawdown: number;
  stopLossPercentage: number;
  positionSizingMethod: string;
  maxCorrelation: number;
  maxSectorExposure: number;
  volatilityAdjustment: boolean;
}

export interface TemplateRiskManagement {
  maxPositionSize: number;
  maxOpenPositions: number;
  maxDailyLoss: number;
  maxWeeklyLoss: number;
  maxDrawdown: number;
  stopLossPercentage: number;
  positionSizingMethod: string;
  maxCorrelation: number;
  maxSectorExposure: number;
  volatilityAdjustment: boolean;
}

export interface TemplateRiskManagementInput {
  maxPositionSize: number;
  maxOpenPositions: number;
  maxDailyLoss: number;
  maxWeeklyLoss: number;
  maxDrawdown: number;
  stopLossPercentage: number;
  positionSizingMethod: string;
  maxCorrelation: number;
  maxSectorExposure: number;
  volatilityAdjustment: boolean;
}

// Enums
export enum ScenarioCategory {
  DAY_TRADING = "DAY_TRADING",
  SWING_TRADING = "SWING_TRADING",
  POSITION_TRADING = "POSITION_TRADING",
  SCALPING = "SCALPING",
  ARBITRAGE = "ARBITRAGE",
  MEAN_REVERSION = "MEAN_REVERSION",
  MOMENTUM = "MOMENTUM",
  BREAKOUT = "BREAKOUT",
  REVERSAL = "REVERSAL",
  CUSTOM = "CUSTOM",
}

export enum RiskLevel {
  CONSERVATIVE = "CONSERVATIVE",
  MODERATE = "MODERATE",
  AGGRESSIVE = "AGGRESSIVE",
  EXTREME = "EXTREME",
}

export enum TimeFrame {
  INTRADAY = "INTRADAY",
  OVERNIGHT = "OVERNIGHT",
  WEEKLY = "WEEKLY",
  MONTHLY = "MONTHLY",
  QUARTERLY = "QUARTERLY",
  YEARLY = "YEARLY",
}

export enum RuleType {
  TECHNICAL_INDICATOR = "TECHNICAL_INDICATOR",
  PRICE_ACTION = "PRICE_ACTION",
  VOLUME_ANALYSIS = "VOLUME_ANALYSIS",
  TIME_BASED = "TIME_BASED",
  NEWS_EVENT = "NEWS_EVENT",
  CUSTOM = "CUSTOM",
}

export enum ExitType {
  STOP_LOSS = "STOP_LOSS",
  TAKE_PROFIT = "TAKE_PROFIT",
  TRAILING_STOP = "TRAILING_STOP",
  TIME_BASED = "TIME_BASED",
  SIGNAL_BASED = "SIGNAL_BASED",
  CUSTOM = "CUSTOM",
}

export enum TemplateDifficulty {
  BEGINNER = "BEGINNER",
  INTERMEDIATE = "INTERMEDIATE",
  ADVANCED = "ADVANCED",
  EXPERT = "EXPERT",
}

export enum TimeHorizon {
  SHORT = "SHORT",
  MEDIUM = "MEDIUM",
  LONG = "LONG",
  VERY_LONG = "VERY_LONG",
}

// API Response Types
export interface ApiResponse<T> {
  data: T;
  message?: string;
  success: boolean;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ScenarioStats {
  total: number;
  active: number;
  draft: number;
  completed: number;
  templates: number;
}

export interface TemplateCategory {
  name: string;
  count: number;
  description: string;
}

export interface TemplateValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  template: any;
}

export interface TemplateUsageStats {
  templateId: string;
  templateName: string;
  usageCount: number;
  averageRating: number;
  totalRatings: number;
  lastUsed: Date;
  successRate: number;
  averageReturn: number;
}
